import {
  ACTION_MOBILE_NAV_HEADER,
  ACTION_MOBILE_NAV_MENU,
  CATEGORY_NAV,
} from 'utils/analytics/eventTypesConstants';
import { ACTION_OPEN_OMNISEARCH } from 'utils/analytics/eventTypeConstantsAmplitude';
import {
  Alert,
  DesktopHeader,
  DesktopHeaderProps,
  Header,
  HeaderProps,
  Link,
  ShrinkingHeaderButton,
} from 'omakase';
import { GiftCardBalanceDialogContainer } from 'components/header/GiftCardBalanceDialogContainer';
import { IconMigrationUtil } from 'omakase/utils/IconMigrationUtil';
import { IconSearch } from '@tock/icons';
import { IconSearch as IconSearchLegacy } from 'ui/icons';
import { MobileSiteMenuImpl } from 'components/site-menu/MobileSiteMenuImpl';
import { PatronLocationContainer } from 'components/patron-location/PatronLocationContainer';
import { PatronLocationGhostButton } from 'components/patron-location/PatronLocationGhostButton';
import { SearchBarOption, SearchBarOptionOrSectionDivider } from 'utils/searchBar';
import { SearchNavHeaderDesktop } from 'components/nav-search/SearchNavHeaderDesktop';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { allMetroAreasSelector } from 'selectors/appSelector';
import { core } from '@tock/coretypes';
import { css } from '@emotion/react';
import { currentMetroAreaSelector } from 'selectors/metroAreaSelectors';
import { headerBase, stickyHeader } from 'components/header/omakaseHeaderCssDefs';
import { isIOSNative } from 'utils/deviceUtils';
import { parse } from '@tock/query-string';
import { useAmplitudeSendEvent } from 'utils/analytics/AnalyticsContext';
import { useFeatureFlagForConsumer } from 'featureFlags/useConsumerFeatureFlag';
import { useHistory, useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import DesktopHeaderNavigateButtons from 'components/header/DesktopHeaderNavigateButtons';
import FeatureFlag from '@tock/shared/utils/featureFlagContext';
import React, { useState } from 'react';
import ResyNetworkBanner from 'components/header/ResyNetworkBanner';
import SearchNav from 'components/nav-search/SearchNav';
import _ from 'lodash';
import searchBarSuggestionsSelector, {
  searchBarSuggestionsSectionedSelector,
} from 'selectors/searchBarSuggestionsSelector';

interface ConnectProps {
  app: StoreType['app'];
  allMetroAreas: core.IMetroArea[];
  currentMetroArea: core.MetroArea | undefined;
  currentSearch: string;
  sectionedSearchBarSuggestions: SearchBarOptionOrSectionDivider[];
  searchBarSuggestions: SearchBarOption[];
  navigation: StoreType['navigation'];
  patron: core.IPatron | undefined;
}

// Acts as a thin wrapper around the Header component above,
// and as an action dispatch point for related actions.
export interface OmakaseHeaderProps {
  additionalMobileContent?: React.ReactNode;
  additionalDesktopContent?: React.ReactNode;
  hasShadow?: boolean;
  isSticky?: boolean;
}

// As a heuristic, do not show titles above this length in characters, as
// they would likely have to be abbreviated.
const MAX_TITLE_LENGTH = 32;

// Although, if no right-side buttons are being shown, we can bump this up slightly:
const MAX_TITLE_LENGTH_NO_RIGHT_BUTTONS = 38;

const HAMBURGER_EXCLUSION_LIST = [
  '/checkout/',
  '/gift/card',
  '/profile/reservations/',
  '/reschedule/',
  '/signup',
  '/login',
  '/receipt/cancel',
];

const IOS_NATIVE_HEADER_EXCLUSION_LIST = ['/receipt'];

const DESKTOP_ACCOUNT_CONTROLS_EXCLUSION_LIST = ['/signup', '/login'];

function isOnExclusionList(pathname: string, exclusionList: string[]) {
  return _.some(exclusionList, (exclusionToken) => pathname.includes(exclusionToken));
}

type Props = OmakaseHeaderProps;

const OmakaseHeader: React.FC<Props> = ({
  additionalDesktopContent,
  additionalMobileContent,
  hasShadow = true,
  isSticky = true,
}) => {
  const amplitudeSendEvent = useAmplitudeSendEvent();
  const {
    app,
    allMetroAreas,
    currentMetroArea,
    currentSearch,
    sectionedSearchBarSuggestions,
    searchBarSuggestions,
    navigation,
    patron,
  } = useSelector(mapStateToProps);

  const { pathname, search } = useLocation();
  const history = useHistory();

  const hasExternalOrNoReferrer =
    typeof document !== 'undefined' &&
    (!document.referrer || !document.referrer.startsWith(window.location.origin));
  const hideBack = (isIOSNative() && history.length === 1) || hasExternalOrNoReferrer;

  const isHome = pathname === '/';

  const {
    actions: {
      discover: { autocompleteBusinessSuggestions },
      search: { clearSearchResults, setCurrentSearch },
      metroArea: { selectMetroArea },
    },
  } = useActions();

  const [searchOpen, setSearchOpen] = React.useState(false);
  const [hideBanner, setHideBanner] = useState<boolean>(false);
  const onSearchClicked = React.useCallback(() => {
    window.analytics?.sendEvent(CATEGORY_NAV, ACTION_MOBILE_NAV_HEADER, `Search button engaged`);
    amplitudeSendEvent(ACTION_OPEN_OMNISEARCH);

    // If clicking on search, we make sure to clear out any past search records before
    // proceeding.
    setCurrentSearch('');

    setSearchOpen(true);
  }, [setCurrentSearch, amplitudeSendEvent]);

  const [menuOpen, setMenuOpen] = React.useState(false);
  const isDisplayPrivacyAlertEnabled = useFeatureFlagForConsumer(
    'CONSUMER_DISPLAY_PRIVACY_UPDATE_ALERT'
  );

  const showMenu = React.useCallback(() => {
    setMenuOpen(true);
    if (window.analytics) {
      window.analytics.sendEvent(
        CATEGORY_NAV,
        ACTION_MOBILE_NAV_MENU,
        'Menu opened with hamburger button'
      );
    }
  }, []);
  const hideMenu = React.useCallback(() => {
    setMenuOpen(false);
  }, []);

  const handleBusinessAutoComplete = (value: string) => {
    let coordinates = undefined;
    if (currentMetroArea?.lat && currentMetroArea?.lng) {
      coordinates = { lat: currentMetroArea.lat, lng: currentMetroArea.lng };
    }
    return autocompleteBusinessSuggestions(value, coordinates);
  };

  const isAuthenticated = !!app.jwtToken;
  const isPartner = !!app.activePartnerRole?.partner;

  const {
    isBusinessProfile,
    goBackTo,
    hasBack,
    hasCityPicker,
    hasClose,
    hasLogo,
    onClose,
    title,
    titleLeft,
  } = navigation;

  const firstName = patron?.firstName ?? undefined;
  const profileImageLink = patron?.imageUrl ?? undefined;

  const backOnClick = React.useCallback(() => history.goBack(), [history]);

  const tockLogoContent: HeaderProps['leftContent'] = {
    type: 'TockLogo',
  };

  const visitWasFromTockSource = React.useMemo(
    () => parse(search)['tock_source'] !== undefined,
    [search]
  );

  const leftContent: HeaderProps['leftContent'] =
    // Visiting a business domain cycles through actions where initially there is no back button,
    // per an actions.navigation.set in BusinessRoute componentDidMount()
    //
    // But then in quick succession, ProfileBody's useEffect() calls will follow, and these
    // both end up setting hasBack to true. There will also be a flash of content showing
    // the business name as the title, but this subsequently goes away.
    //
    // This isn't what we want, so we special-case the business-domain, visit-not-from-Tock source
    // case.
    //
    // Really we should revisit and correct this whole sequence.
    isBusinessProfile && !visitWasFromTockSource
      ? tockLogoContent
      : hasBack && !hideBack && titleLeft
        ? {
            type: 'TitleTextWithLeftButton',
            text: titleLeft,
            onClick: goBackTo ? () => history.push(goBackTo) : backOnClick,
          }
        : !hasLogo && !hideBack && hasBack && !goBackTo
          ? {
              type: 'LeftButton',
              onClick: backOnClick,
            }
          : !hasLogo && !hideBack && hasBack && goBackTo
            ? {
                type: 'LeftButton',
                onClick: () => history.push(goBackTo),
              }
            : tockLogoContent;

  const showClose = !!(hasClose && onClose);

  // Usually we show the hamburger button and the search button. We suppress this only if
  // 1) We're showing a close button instead, or
  //
  // 2) The user is in a number of hand-excluded flows, by checking the pathname
  // against an exclusion list. (Implementation above).
  //
  // The exclusion list is partly based on the conditions under which the consumer bottom nav is shown,
  // and expands on them a little besides, including reservations-related paths and
  // some login-related paths as well.
  //
  // Note that /profile/reservations itself will show search/hamburger, but not any subpaths
  // underneath it.
  const onHamburgerExclusionList = isOnExclusionList(pathname, HAMBURGER_EXCLUSION_LIST);
  const hideHeader = isIOSNative() && isOnExclusionList(pathname, IOS_NATIVE_HEADER_EXCLUSION_LIST);
  const showHamburger = !showClose && !onHamburgerExclusionList && !isIOSNative();

  const showSearch = showHamburger;

  const maxLength =
    showClose || showHamburger || showSearch ? MAX_TITLE_LENGTH : MAX_TITLE_LENGTH_NO_RIGHT_BUTTONS;

  const centerContent: HeaderProps['centerContent'] =
    title && title.length <= maxLength
      ? {
          type: 'TitleText',
          text: title,
        }
      : hasCityPicker
        ? {
            type: 'CenterButton',
            button: <PatronLocationContainer initialComponent={PatronLocationGhostButton} />,
            isTakeover: false,
          }
        : {
            type: 'Absent',
          };

  const auxiliaryButtons = [
    <ShrinkingHeaderButton
      aria-label="Search"
      key="Search"
      data-testid="mobile-header-search"
      iconStart={<IconMigrationUtil legacyIcon={<IconSearchLegacy />} newIcon={<IconSearch />} />}
      size="medium"
      variant="ghost"
      onClick={onSearchClicked}
      css={() =>
        isHome &&
        css`
          display: none;
        `
      }
    />,
    [],
  ];

  const auxiliaryContent: HeaderProps['auxiliaryContent'] = showSearch
    ? {
        type: 'AuxiliaryButtons',
        buttons: auxiliaryButtons,
      }
    : {
        type: 'Absent',
      };

  const rightContent: HeaderProps['rightContent'] = showClose
    ? {
        type: 'CloseButton',
        onClick: onClose,
      }
    : showHamburger
      ? {
          type: 'HamburgerButton',
          onClick: showMenu,
        }
      : {
          type: 'Absent',
        };

  const desktopRightContent: DesktopHeaderProps['rightContent'] = isOnExclusionList(
    pathname,
    DESKTOP_ACCOUNT_CONTROLS_EXCLUSION_LIST
  )
    ? {
        type: 'Absent',
      }
    : {
        type: 'AvatarOrAuthenticationButtons',
        isAuthenticated,
        isPartner,
        pathname,
        guestAccountInfo:
          profileImageLink !== undefined && firstName !== undefined
            ? {
                profileImageUrl: profileImageLink,
                name: firstName,
              }
            : undefined,
      };

  const desktopAuxiliaryContent: DesktopHeaderProps['auxiliaryContent'] = onHamburgerExclusionList
    ? {
        type: 'Absent',
      }
    : {
        type: 'AuxiliaryButtons',
        buttons: [
          <DesktopHeaderNavigateButtons metroArea={currentMetroArea} />,
          <SearchNavHeaderDesktop
            key="header-button"
            autocompleteBusinessSuggestions={handleBusinessAutoComplete}
            currentSearch={currentSearch}
            clearSearchResults={clearSearchResults}
            searchResults={searchBarSuggestions}
            setCurrentSearch={setCurrentSearch}
          />,
        ],
        slimLeft: false, // spacing is managed manually
      };

  const desktopRightAuxiliaryContent: DesktopHeaderProps['rightAuxiliaryContent'] = {
    type: 'Absent',
  };

  if (hideHeader) {
    return null;
  }

  const handleHideBanner = () => {
    setHideBanner(true);
  };

  return (
    <header css={[isSticky && stickyHeader, headerBase]} data-testid="tock-header">
      <>
        <ResyNetworkBanner />
        {isDisplayPrivacyAlertEnabled && !hideBanner && (
          <Alert
            action={<Link href={'/privacy'}>Review the policy</Link>}
            severity="primary"
            onClick={handleHideBanner}
          >
            We've updated our Privacy Policy to better explain how we collect, use, and protect your
            data.
          </Alert>
        )}
        <Header
          data-testid="mainheader-mobilewrapper"
          leftContent={leftContent}
          centerContent={centerContent}
          auxiliaryContent={auxiliaryContent}
          rightContent={rightContent}
          additionalContent={additionalMobileContent}
          hasShadow={hasShadow}
          bumpZIndex
        />
        <MobileSiteMenuImpl
          isLoggedIn={isAuthenticated}
          isPartner={isPartner}
          pathname={pathname}
          search={search}
          open={menuOpen}
          onClose={hideMenu}
          patronName={firstName}
          profileImageLink={profileImageLink}
          metroArea={currentMetroArea}
        />
        <SearchNav
          allMetroAreas={allMetroAreas}
          onSearch={handleBusinessAutoComplete}
          clearSearchResults={clearSearchResults}
          currentMetroArea={currentMetroArea}
          currentSearch={currentSearch}
          onSelectMetroArea={(metroArea) => {
            selectMetroArea(metroArea);
            window.analytics.sendEvent(
              'MetroArea',
              'selected from global nav',
              `${metroArea.name}`
            );
          }}
          searchResults={sectionedSearchBarSuggestions}
          setCurrentSearch={setCurrentSearch}
          isDialogOpen={searchOpen}
          onClose={() => {
            setSearchOpen(false);
          }}
        />
        <FeatureFlag doNotShow ifEnabled="GOODBYE_TOCK_GIFT_CARDS">
          <GiftCardBalanceDialogContainer />
        </FeatureFlag>
      </>

      <DesktopHeader
        data-testid="mainheader-desktopwrapper"
        leftContent={{
          type: 'TockLogo',
        }}
        centerContent={{
          type: 'Absent',
        }}
        auxiliaryContent={desktopAuxiliaryContent}
        includeLeftwardDivider={false}
        rightAuxiliaryContent={desktopRightAuxiliaryContent}
        rightContent={desktopRightContent}
        additionalContent={additionalDesktopContent}
        hasShadow={hasShadow}
        bumpZIndex
      />
    </header>
  );
};

function mapStateToProps(state: StoreType): ConnectProps {
  return {
    app: state.app,
    allMetroAreas: allMetroAreasSelector(state),
    currentMetroArea: currentMetroAreaSelector(state),
    currentSearch: state.search.currentSearch,
    sectionedSearchBarSuggestions: searchBarSuggestionsSectionedSelector(state),
    searchBarSuggestions: searchBarSuggestionsSelector(state),
    navigation: state.navigation,
    patron: state.app.patronProfile?.patron ?? undefined,
  };
}

export default OmakaseHeader;
